import React, { useState, useCallback, useRef, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import Navbar from './components/common/Navbar';
import Sidebar from './components/common/Sidebar';
import { SettingsProvider } from './context/SettingsContext';
import { ThemeProvider } from './context/ThemeContext';
import { SessionProvider } from './context/SessionContext';
import { LanguageProvider } from './context/LanguageContext';
import { TimezoneProvider } from './context/TimezoneContext';
import { AutoSaveProvider } from './context/AutoSaveContext';
import { NotificationSystemProvider } from './context/NotificationSystemContext';
import { SecurityProvider } from './context/SecurityContext';
import { WebhookProvider } from './context/WebhookContext';
import { PerformanceProvider } from './context/PerformanceContext';
// Heavy contexts — only mounted on Settings page, not app-wide
import { PipelineProvider } from './context/PipelineContext';
import { ConnectionProvider } from './context/ConnectionContext';
import { KafkaProvider } from './context/KafkaContext';
import './styles/themes.css';
// Must follow themes.css: it re-scopes the app-shell rules that leak onto the
// pre-login pages, and relies on source order where specificity ties.
import './styles/public-theme.css';
// Terminal page removed — now a floating panel (lazy-loaded to defer CodeMirror)
const FloatingDock = lazy(() => import('./components/thinking-prompt/FloatingDock'));
import ProtectedRoute from './components/auth/ProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import TrialBanner from './components/subscription/TrialBanner';
import CookieConsent from './components/common/CookieConsent';
import EnvironmentBanner from './components/common/EnvironmentBanner';
import { isOnPrem, getFeatureFlag } from './utils/featureFlags';

// Auth-flow pages (keep static for fast first paint)
import AuthPage from './pages/AuthPage';
import LandingPage from './pages/LandingPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';

// Lazy-loaded pages (only downloaded when navigated to)
const HomePage = lazy(() => import('./pages/HomePage'));
const ConnectionsPage = lazy(() => import('./pages/ConnectionsPage'));
const CreatePipelinePage = lazy(() => import('./pages/CreatePipelinePage'));
const DataQualityDashboard = lazy(() => import('./pages/DataQualityDashboard'));
const DebuggingDashboard = lazy(() => import('./pages/DebuggingDashboard'));
const OptimizationPage = lazy(() => import('./pages/OptimizationPage'));
const MonitorPipelinesPage = lazy(() => import('./pages/MonitorPipelinesPage'));
const SchedulePipelinesPage = lazy(() => import('./pages/SchedulePipelinesPage'));
const ApiDebugPage = lazy(() => import('./pages/ApiDebugPage'));
const MicroservicesPage = lazy(() => import('./pages/MicroservicesPage'));
const KafkaMessagingPage = lazy(() => import('./pages/KafkaMessagingPage'));
const SqlOperationsPage = lazy(() => import('./pages/SqlOperationsPage'));
const DocumentationPage = lazy(() => import('./pages/DocumentationPage'));
const FilePipelinesPage = lazy(() => import('./pages/FilePipelinesPage'));
const DatabasePipelinesPage = lazy(() => import('./pages/DatabasePipelinesPage'));
const PipelineSequenceListPage = lazy(() => import('./pages/PipelineSequenceListPage'));
const PipelineSequenceBuilderPage = lazy(() => import('./pages/PipelineSequenceBuilderPage'));
const PipelineSequenceMonitoringDashboard = lazy(() => import('./components/pipeline-sequences/PipelineSequenceMonitoringDashboard'));
const TestFilePipelinesPage = lazy(() => import('./pages/TestFilePipelinesPage'));
const MinimalTestPage = lazy(() => import('./pages/MinimalTestPage'));
const IsolatedTestPage = lazy(() => import('./pages/IsolatedTestPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentFailure = lazy(() => import('./pages/PaymentFailure'));
const LogViewerPage = lazy(() => import('./pages/LogViewerPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const ConfigPromotionPage = lazy(() => import('./pages/ConfigPromotionPage'));
const EmailVerificationPage = lazy(() => import('./pages/EmailVerificationPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const SecurityDashboard = lazy(() => import('./pages/SecurityDashboard'));
const SecurityActivityPage = lazy(() => import('./pages/SecurityActivityPage'));
const MfaSettingsPage = lazy(() => import('./pages/MfaSettingsPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const FileExplorerPage = lazy(() => import('./pages/FileExplorerPage'));
const GlobalVariablesPage = lazy(() => import('./pages/GlobalVariablesPage'));
const AgentsPage = lazy(() => import('./pages/AgentsPage'));
const WorkspacePage = lazy(() => import('./pages/WorkspacePage'));
const DataModelsPage = lazy(() => import('./pages/DataModelsPage'));

// Suspense fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
  </div>
);

// AppContent component to use router hooks
const AppContent = () => {
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [showTrialBanner, setShowTrialBanner] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  const dockRef = useRef(null);

  // All hooks must be called before any conditional returns (React rules of hooks)
  // `state` is an optional react-router location state — used e.g. when the chat
  // agent opens a designer pre-seeded with a draft (see ThinkingPromptPanel).
  const handleNavigate = useCallback((pageName, state) => {
    // Intercept navigation to Thinking Prompt / CodeGen — open floating panel instead
    if (pageName === 'molecule-prompt') {
      dockRef.current?.openPanel('terminal');
      return;
    }
    if (pageName === 'ai-jobs') {
      dockRef.current?.openPanel('codegen');
      return;
    }
    if (pageName === 'ai-chat' || pageName === 'heartbeat') {
      dockRef.current?.openPanel('ai-chat');
      return;
    }
    navigate(`/${pageName}`, state ? { state } : undefined);
  }, [navigate]);

  const toggleSidebar = useCallback(() => {
    setSidebarExpanded(prev => !prev);
  }, []);

  // Publish sidebar width as a CSS var so the docked Workspace panel
  // (rendered at document root) can align its left edge.
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--app-sidebar-width',
      sidebarExpanded ? '256px' : '64px'
    );
  }, [sidebarExpanded]);

  const getCurrentPage = useCallback(() => {
    const path = location.pathname.substring(1) || 'home';
    return path;
  }, [location.pathname]);

  const isWorkspaceRoute = location.pathname === '/workspace';

  // Check if we're on the auth page, OAuth callback, payment pages, email-related pages, or legal page
  // Pages rendered without the app navbar/sidebar layout
  const isAuthPage = location.pathname === '/' ||
                    location.pathname === '/auth' ||
                    location.pathname === '/oauth/callback' ||
                    location.pathname === '/verify-email' ||
                    location.pathname === '/forgot-password' ||
                    location.pathname === '/reset-password' ||
                    location.pathname === '/payment/success' ||
                    location.pathname === '/payment/failure' ||
                    location.pathname === '/legal' ||
                    location.pathname === '/pricing';

  // Show loading while auth is being verified (prevents flash of homepage before redirect)
  // Use white background to match login page for seamless transition
  if (loading && !isAuthPage) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#000000' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
      </div>
    );
  }

  // If we're on the auth page, render just the auth component with no layout
  if (isAuthPage) {
    return (
      <div className="min-h-screen" style={{ background: '#000000' }}>
        <Suspense fallback={<PageLoader />}>
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
              <Route path="/verify-email" element={<EmailVerificationPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              {!isOnPrem() && <Route path="/payment/success" element={<PaymentSuccess />} />}
              {!isOnPrem() && <Route path="/payment/failure" element={<PaymentFailure />} />}
              <Route path="/legal" element={<LegalPage />} />
              {!isOnPrem() && <Route path="/pricing" element={<PricingPage />} />}
            </Routes>
          </AnimatePresence>
        </Suspense>
      </div>
    );
  }

  // Otherwise render the full app layout with navbar and sidebar
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <EnvironmentBanner />
      <Navbar sidebarExpanded={sidebarExpanded} className="flex-shrink-0 border-b border-gray-200" />

      {/* Trial Banner - Shows only for trial users in cloud mode */}
      {!isOnPrem() && showTrialBanner && <TrialBanner user={user} onDismiss={() => setShowTrialBanner(false)} />}

      <div className="flex flex-1 pt-16 relative">
        {/* Sidebar - always visible, no hover behavior */}
        <div className="fixed top-16 left-0 bottom-0 z-30">
          <Sidebar
            onNavigate={handleNavigate}
            currentPage={getCurrentPage()}
            isExpandedOverride={sidebarExpanded}
            onToggleExpand={toggleSidebar}
          />
        </div>

        <div
          className={`flex-1 transition-[margin] duration-200 ease-out ${
            sidebarExpanded ? 'ml-64' : 'ml-16'
          }`}
          style={{
            paddingRight: 'var(--workspace-dock-right, 0)',
            paddingBottom: 'var(--workspace-dock-bottom, 0)',
            transition: 'padding 200ms ease-out, margin 200ms ease-out',
          }}
        >
          <main
            className={`h-[calc(100vh-4rem)] overflow-y-auto transition-all duration-200 ease-out ${
              sidebarExpanded ? 'bg-gray-50 rounded-l-xl shadow-inner' : 'bg-gray-100'
            }`}
          >
            <div className="p-4">
              <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/home" element={
                  <ProtectedRoute>
                    <HomePage onNavigate={handleNavigate} />
                  </ProtectedRoute>
                } />
                <Route path="/connections" element={
                  <ProtectedRoute>
                    <ConnectionsPage />
                  </ProtectedRoute>
                } />
                <Route path="/file-explorer" element={
                  <ProtectedRoute>
                    <FileExplorerPage />
                  </ProtectedRoute>
                } />
                <Route path="/global-variables" element={
                  <ProtectedRoute>
                    <GlobalVariablesPage />
                  </ProtectedRoute>
                } />
                <Route path="/data-models" element={
                  <ProtectedRoute>
                    <DataModelsPage />
                  </ProtectedRoute>
                } />
                <Route path="/create-pipeline" element={
                  <ProtectedRoute>
                    <CreatePipelinePage />
                  </ProtectedRoute>
                } />
                <Route path="/file-pipelines" element={
                  <ProtectedRoute>
                    <FilePipelinesPage />
                  </ProtectedRoute>
                } />
                <Route path="/test-file-pipelines" element={
                  <ProtectedRoute>
                    <TestFilePipelinesPage />
                  </ProtectedRoute>
                } />
                <Route path="/minimal-test" element={
                  <ProtectedRoute>
                    <MinimalTestPage />
                  </ProtectedRoute>
                } />
                <Route path="/isolated-test" element={
                  <ProtectedRoute>
                    <IsolatedTestPage />
                  </ProtectedRoute>
                } />

                <Route path="/monitor-pipelines" element={
                  <ProtectedRoute>
                    <MonitorPipelinesPage />
                  </ProtectedRoute>
                } />
                <Route path="/schedule-pipelines" element={
                  <ProtectedRoute>
                    <SchedulePipelinesPage />
                  </ProtectedRoute>
                } />
                <Route path="/kafka" element={
                  <ProtectedRoute>
                    <KafkaMessagingPage />
                  </ProtectedRoute>
                } />
                {/* Redirect from old path to new consolidated path */}
                <Route path="/kafka-messaging" element={
                  <ProtectedRoute>
                    <KafkaMessagingPage />
                  </ProtectedRoute>
                } />
                <Route path="/sql-operations" element={
                  <ProtectedRoute>
                    <SqlOperationsPage />
                  </ProtectedRoute>
                } />
                <Route path="/agents" element={
                  <ProtectedRoute>
                    <AgentsPage />
                  </ProtectedRoute>
                } />
                <Route path="/workspace" element={
                  <ProtectedRoute>
                    <WorkspacePage onNavigate={handleNavigate} />
                  </ProtectedRoute>
                } />
                <Route path="/data-quality" element={
                  <ProtectedRoute>
                    <DataQualityDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/debugging" element={
                  <ProtectedRoute>
                    <DebuggingDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/optimization" element={
                  <ProtectedRoute>
                    <OptimizationPage />
                  </ProtectedRoute>
                } />
                                <Route path="/api-debug" element={
                  <ProtectedRoute requireAdmin={true}>
                    <ApiDebugPage />
                  </ProtectedRoute>
                } />
                {/* API Pipeline route removed */}
                <Route path="/microservices" element={
                  <ProtectedRoute>
                    <MicroservicesPage />
                  </ProtectedRoute>
                } />
                <Route path="/database-pipelines" element={
                  <ProtectedRoute>
                    <DatabasePipelinesPage />
                  </ProtectedRoute>
                } />
                <Route path="/pipeline-sequences" element={
                  <ProtectedRoute>
                    <PipelineSequenceListPage />
                  </ProtectedRoute>
                } />
                <Route path="/pipeline-sequences/builder" element={
                  <ProtectedRoute>
                    <PipelineSequenceBuilderPage />
                  </ProtectedRoute>
                } />
                <Route path="/pipeline-sequences/monitoring" element={
                  <ProtectedRoute>
                    <PipelineSequenceMonitoringDashboard />
                  </ProtectedRoute>
                } />

                {/* molecule-prompt and ai-jobs are now in the floating panel */}
                <Route path="/documentation" element={
                  <ProtectedRoute>
                    <DocumentationPage />
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <PipelineProvider>
                      <ConnectionProvider>
                        <KafkaProvider>
                          <SettingsPage />
                        </KafkaProvider>
                      </ConnectionProvider>
                    </PipelineProvider>
                  </ProtectedRoute>
                } />
                {!isOnPrem() && <Route path="/subscription" element={
                  <ProtectedRoute>
                    <SubscriptionPage />
                  </ProtectedRoute>
                } />}
                <Route path="/logs" element={
                  <ProtectedRoute>
                    <LogViewerPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin-dashboard" element={
                  <ProtectedRoute requireAdmin={true}>
                    <AdminDashboardPage />
                  </ProtectedRoute>
                } />
                {/* Config Promotion - On-Prem Environment Promotion */}
                {isOnPrem() && <Route path="/config-promotion" element={
                  <ProtectedRoute>
                    <ConfigPromotionPage />
                  </ProtectedRoute>
                } />}
                {/* Security Event Monitoring - Phase 10 */}
                <Route path="/admin/security-dashboard" element={
                  <ProtectedRoute requireAdmin={true}>
                    <SecurityDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/security-activity" element={
                  <ProtectedRoute>
                    <SecurityActivityPage />
                  </ProtectedRoute>
                } />
                <Route path="/mfa-settings" element={
                  <ProtectedRoute>
                    <MfaSettingsPage />
                  </ProtectedRoute>
                } />
              </Routes>
              </Suspense>
            </div>
          </main>
        </div>
      </div>

      {/* Floating terminal dock — lazy-loaded. Hidden on /workspace (the
          panel IS the page there, so the dock would be redundant). */}
      {!isWorkspaceRoute && (
        <Suspense fallback={null}>
          <FloatingDock ref={dockRef} onNavigate={handleNavigate} />
        </Suspense>
      )}
    </div>
  );
};

// Main App component wrapped with Router
const App = () => {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
        <SettingsProvider>
          <SecurityProvider>
            <SessionProvider>
              <LanguageProvider>
                <TimezoneProvider>
                  <AutoSaveProvider>
                    <NotificationSystemProvider>
                      <WebhookProvider>
                        <PerformanceProvider>
                                <ThemeProvider>
                                  <NotificationsProvider>
                                    <Toaster
                                      position="top-right"
                                      containerStyle={{ zIndex: 9999 }}
                                      toastOptions={{
                                        style: {
                                          zIndex: 9999,
                                          background: 'rgba(255, 255, 255, 0.7)',
                                          backdropFilter: 'blur(20px) saturate(180%)',
                                          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                                          border: '1px solid rgba(255, 255, 255, 0.5)',
                                          boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.6), 0 4px 24px -4px rgba(15,23,42,0.12), 0 12px 48px -8px rgba(15,23,42,0.08)',
                                          borderRadius: '14px',
                                          padding: '12px 16px',
                                          color: '#1e293b',
                                          fontSize: '14px',
                                          fontWeight: '500',
                                        },
                                        success: {
                                          style: {
                                            borderLeft: '3px solid #10b981',
                                          },
                                          iconTheme: { primary: '#10b981', secondary: '#ffffff' },
                                        },
                                        error: {
                                          style: {
                                            borderLeft: '3px solid #ef4444',
                                          },
                                          iconTheme: { primary: '#ef4444', secondary: '#ffffff' },
                                        },
                                        duration: 3000,
                                      }}
                                    />
                                    <AppContent />
                                    <CookieConsent />
                                  </NotificationsProvider>
                                </ThemeProvider>
                        </PerformanceProvider>
                      </WebhookProvider>
                    </NotificationSystemProvider>
                  </AutoSaveProvider>
                </TimezoneProvider>
              </LanguageProvider>
            </SessionProvider>
          </SecurityProvider>
        </SettingsProvider>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
};

export default App;
